import * as React from 'react';

import { ModalHeader as EmbarkModalHeader, Modal } from '@emburse/ui-core';

import { CardStatementUploadWizardStart } from '@ReactComponents/BulkUploadWizardStart';
import { IProgressBarPage, ProgressBar } from '@ReactComponents/ProgressBar';
import { Wizard } from '@ReactComponents/Wizard/Wizard';

import { ILinkedAccountModel } from '@Shared/models';

import { ConnectedManualUploadCardProgramCardMapping } from './components/ManualUploadCardProgramCardMapping';
import { ManualUploadCardProgramCreate } from './components/ManualUploadCardProgramCreate';
import { ConnectedManualUploadCardProgramSummary } from './components/ManualUploadCardProgramSummary';

// Components

export type ManualUploadCardProgramPanelKeys = 'createProgram' | 'start' | 'summary' | 'card_mapping' | 'preview' | 'applying';

interface IManualUploadCardProgramWizardProps {
  onSuccess: () => void;
  onFailure: (message?: string) => void;
  onReceiveProgram?: (cardProgram: ILinkedAccountModel) => void;
  uploadType?: 'card_statement';
  cardProgram?: ILinkedAccountModel;
}

interface IManualUploadCardProgramModalProps extends IManualUploadCardProgramWizardProps {
  closeModal: () => void;
  firstPanel?: ManualUploadCardProgramPanelKeys;
}

interface IManualUploadCardProgramModalState {
  initialized: boolean;
  headerText?: string;
  cardProgram?: ILinkedAccountModel;
  isCreatingCardProgram?: boolean;
  currentPageId?: ManualUploadCardProgramPanelKeys;
}

export class ManualUploadCardProgramModal extends React.Component<IManualUploadCardProgramModalProps, IManualUploadCardProgramModalState> {
  static getDerivedStateFromProps(props: IManualUploadCardProgramModalProps, lastState: IManualUploadCardProgramModalState) {
    if (!lastState.initialized) {
      if (!props.firstPanel) {
        return {
          headerText: 'Add Corporate Card Program',
          isCreatingCardProgram: true,
          currentPageId: 'createProgram',
          initialized: true
        };
      }

      return {
        headerText: 'Upload Transactions',
        cardProgram: props.cardProgram,
        isCreatingCardProgram: false,
        initialized: true
      };
    }
    return null;
  }

  constructor(props: IManualUploadCardProgramModalProps) {
    super(props);
    this.state = { initialized: false };
  }

  onPanelChange(nextPanel: ManualUploadCardProgramPanelKeys) {
    this.setState({
      currentPageId: nextPanel
    });

    if (nextPanel !== 'createProgram') {
      this.setState({
        headerText: 'Upload Transactions'
      });
    } else {
      this.setState({
        headerText: 'Add Corporate Card Program'
      });
    }
  }

  /**
   * Renders the progress bar section and header for the current panel
   * @param currentPageId - the id of the current page
   * @param pages - a collection of pages
   */
  renderProgressBarSection(currentPageId: ManualUploadCardProgramPanelKeys, pages: IProgressBarPage<ManualUploadCardProgramPanelKeys>[]) {
    let sectionHeader;
    switch (currentPageId) {
      case 'createProgram':
        sectionHeader = 'Program Info';
        break;
      case 'start':
        sectionHeader = 'Statement Upload';
        break;
      case 'card_mapping':
        sectionHeader = 'Card Holders';
        break;
      case 'summary': // this might be a little misleading as it's not read only, transactions-select maybe?
        sectionHeader = 'Import Transactions';
        break;
    }

    return (
      <div className="section-header">
        <div className="section-header-label">{sectionHeader}</div>
        <div className="progress-bar">
          <ProgressBar pages={pages} currentPageId={currentPageId} />
        </div>
      </div>
    );
  }

  render() {
    const { closeModal, firstPanel } = this.props;
    const { headerText, cardProgram } = this.state;

    const panelComponents: Record<ManualUploadCardProgramPanelKeys, React.ComponentType<any>> = {
      createProgram: ManualUploadCardProgramCreate,
      start: CardStatementUploadWizardStart,
      summary: ConnectedManualUploadCardProgramSummary,
      card_mapping: ConnectedManualUploadCardProgramCardMapping,
      preview: null, // We likely aren't really using this panel in the card statement upload flow, or rather, summary and preview are combined
      applying: null
    };

    const pages: IProgressBarPage<ManualUploadCardProgramPanelKeys>[] = [
      { id: 'createProgram' },
      { id: 'start' },
      { id: 'card_mapping' },
      { id: 'summary' }
    ];

    const progressBarSection = this.state.isCreatingCardProgram ? this.renderProgressBarSection(this.state.currentPageId, pages) : null;

    const embarkModalHeader = <EmbarkModalHeader title={headerText} onCloseClicked={closeModal} />;

    return (
      <Modal
        open={true}
        onClose={closeModal}
        modalHeader={embarkModalHeader}
        style={{ width: 800, margin: '0 auto', maxWidth: '80%', minWidth: 500 }}
      >
        {progressBarSection}
        <Wizard<ManualUploadCardProgramPanelKeys, IManualUploadCardProgramWizardProps>
          firstPanel={firstPanel || 'createProgram'}
          uploadType="card_statement"
          cardProgram={cardProgram}
          panelComponents={panelComponents}
          onSuccess={this.props.onSuccess}
          onFailure={this.props.onFailure}
          onPanelChange={(nextPanel) => this.onPanelChange(nextPanel)}
          onReceiveProgram={(program) => this.setState({ cardProgram: program })}
        />
      </Modal>
    );
  }
}
